import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    OnDestroy,
    output,
    signal,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FeeWaiverDatesHelper, WaiversHelper } from '@features/groups/fee-waivers/_core/helpers';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TimezoneHelper } from '@shared/helpers';
import { FeeWaiverDateInterval, FeeWaiverStatusEnum } from '@shared/types/waivers';
import { DatepickerFcComponent, FormErrorComponent } from '@ui-kit/form';
import { MessageComponent } from '@ui-kit/miscs';
import { Subscription } from 'rxjs';

@Component({
    selector: 'fee-waiver-datepicker-fc',
    imports: [DatepickerFcComponent, FormErrorComponent, TranslateModule, MessageComponent],
    templateUrl: './fee-waiver-datepicker-fc.component.html',
    styleUrl: './fee-waiver-datepicker-fc.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class FeeWaiverDatepickerFcComponent implements OnDestroy {
    private translate = inject(TranslateService);

    id = input.required<string>();
    form = input.required<{
        formGroup: FormGroup<any>;
        startDate: FormControl<Date | null>;
        endDate: FormControl<Date | null>;
    }>();

    feeWaiverStatus = input<FeeWaiverStatusEnum | null>(null);
    showClear = input<boolean>(false);
    displayIntervalInfo = input<boolean>(true);
    isBulkEditInterval = input<boolean>(false);

    initialEndDate = signal<Date | null>(null);
    isExpiringWaiver = signal<boolean>(false);
    feeWaiverInterval = signal<FeeWaiverDateInterval | null>(null);
    feeWaiverIntervalInfo = computed(() => {
        const p = this.feeWaiverInterval();

        if (!p) {
            return '';
        }
        const { key, params } = WaiversHelper.getFeeWaiverIntervalPeriodInfoTranslation(p, this.isBulkEditInterval());
        return this.translate.instant(key, params);
    });

    readonly waiverWithMonthInPast = computed(() => FeeWaiverDatesHelper.isOngoingWaiver(this.startDate.value));

    readonly firstDayOfCurrentMonth = TimezoneHelper.firstDayOfCurrentUTCMonth();
    readonly lastMonthDate = TimezoneHelper.firstDayOfPreviousMonth(TimezoneHelper.firstDayOfCurrentUTCMonth());

    readonly waiverStatusEnum = FeeWaiverStatusEnum;
    readonly subscriptions = new Subscription();

    expiringFeeWaiver = output<boolean>();

    constructor() {
        effect(() => {
            const startDate = this.startDate.value;
            const endDate = this.endDate.value;

            if (startDate && endDate && this.displayIntervalInfo()) {
                this.feeWaiverInterval.set(FeeWaiverDatesHelper.buildFeeWaiverDateInterval(startDate, endDate));
            }

            if (!this.initialEndDate()) {
                this.initialEndDate.set(endDate);
            }
        });

        effect(() => {
            const startDate = this.startDate;
            const endDate = this.endDate;

            if (!startDate || !endDate) {
                return;
            }

            if (!this.displayIntervalInfo()) {
                return;
            }

            this.subscriptions.add(
                startDate.valueChanges.subscribe(value => {
                    if (value && endDate.value) {
                        this.feeWaiverInterval.set(
                            FeeWaiverDatesHelper.buildFeeWaiverDateInterval(value, endDate.value)
                        );
                    }
                })
            );

            this.subscriptions.add(
                endDate.valueChanges.subscribe(value => {
                    if (value && startDate.value) {
                        this.updateExpiringWaiver();
                        this.feeWaiverInterval.set(
                            FeeWaiverDatesHelper.buildFeeWaiverDateInterval(startDate.value, value)
                        );
                    }
                })
            );
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private updateExpiringWaiver(): void {
        this.isExpiringWaiver.set(
            FeeWaiverDatesHelper.isExpiringWaiverEndDateUpdated(
                this.initialEndDate() ?? null,
                this.endDate.getRawValue() ?? null,
                TimezoneHelper.firstDayOfCurrentUTCMonth()
            )
        );
        this.expiringFeeWaiver.emit(this.isExpiringWaiver());
    }

    get formGroup(): FormGroup<any> {
        return this.form().formGroup;
    }

    get startDate(): FormControl<Date | null> {
        return this.form().startDate;
    }

    get endDate(): FormControl<Date | null> {
        return this.form().endDate;
    }
}
